import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUpRight, ChevronLeft, ChevronRight, ArrowLeft, Folder } from 'lucide-react';
import Container from '../components/common/Container';
import { useDispatch, useSelector } from 'react-redux';
import { getAllProjects } from '../redux/slice/project.slice';
import { getAllCategories } from '../redux/slice/category.slice';

const FilterButton = ({ active, label, onClick }) => (
    <button
        onClick={onClick}
        className={`px-4 sm:px-6 md:px-8 py-2 md:py-3 rounded-full text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] font-bold transition-all duration-300 min-w-[120px] sm:min-w-[140px] md:min-w-[160px] border ${active
                ? 'bg-secondary text-white border-secondary shadow-md'
                : 'bg-transparent text-secondary/70 border-secondary/20 hover:border-secondary hover:text-secondary'
            }`}
    >
        {label}
    </button>
);

const ProjectCard = ({ project, onClick }) => {
    const mainImage = project.images?.[0]?.url;
    const imageCount = project.images?.length || 0;

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -10 }}
            className="group cursor-pointer relative h-full"
            onClick={onClick}
        >
            <div className="relative aspect-[4/5] sm:aspect-[4/3] overflow-hidden rounded-xl bg-gray-100 shadow-2xl">
                {/* Folder Style Stack Effect */}
                <div className="absolute inset-0 bg-secondary/10 transform rotate-3 rounded-3xl transition-transform duration-500 group-hover:rotate-6" />
                <div className="absolute inset-0 bg-secondary/5 transform -rotate-2 rounded-3xl transition-transform duration-500 group-hover:-rotate-4" />

                <img
                    src={mainImage}
                    alt={project.title}
                    className="absolute inset-0 w-full h-full object-cover rounded-xl transition-transform duration-700 group-hover:scale-110"
                />

                {/* Dynamic Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-60 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-2 md:p-4">
                    <div className="overflow-hidden">
                        <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.1 }}
                        >
                            {/* <div className="flex items-center gap-2 mb-2">
                                <span className="h-px w-6 bg-secondary"></span>
                                <p className="text-secondary text-[10px] uppercase tracking-[0.2em] font-bold">Project</p>
                            </div> */}
                            <h3 className="text-white text-2xl md:text-3xl font-serif leading-tight">{project.title}</h3>

                            <div className="flex items-center justify-between mt-auto pt-1 border-t border-white/10">
                                <div className="flex items-center gap-2 text-white/70">
                                    <Folder size={14} className="text-white" />
                                    <span className="text-sm font-light tracking-wider">{imageCount} Images</span>
                                </div>
                                <div className="w-10 h-10 rounded-full bg-secondary/20 backdrop-blur-md flex items-center justify-center border border-white/10 group-hover:bg-secondary transition-colors duration-300">
                                    <ArrowUpRight size={18} className="text-white" />
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const GalleryPage = () => {
    const dispatch = useDispatch();
    const [selectedProject, setSelectedProject] = useState("All");
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [galleryImages, setGalleryImages] = useState([]);
    const [filteredImages, setFilteredImages] = useState([]);

    // Select data from Redux store
    const { projects: rawProjects, loading: projectsLoading } = useSelector((state) => state.project);
    const { categories: rawCategories } = useSelector((state) => state.category);

    useEffect(() => {
        if (rawProjects.length === 0) {
            dispatch(getAllProjects());
        }
        if (rawCategories.length === 0) {
            dispatch(getAllCategories());
        }
    }, [dispatch]);

    // Transform projects data into gallery images
    useEffect(() => {
        if (rawProjects.length > 0) {
            const allImages = rawProjects.flatMap(project => {
                const categoryName = rawCategories.find(c => c._id === project.category)?.name || 'Uncategorized';
                return (project.images || []).map((img, index) => ({
                    id: `${project._id}-${index}`,
                    src: img.url,
                    title: project.title,
                    category: categoryName, // Keep category for display if needed
                    projectTitle: project.title, // Use project title for filtering
                    description: project.description,
                    // Group other images from the same project for the gallery view
                    gallery: project.images ? project.images.map(i => i.url) : [],
                    projectIndex: index
                }));
            });
            setGalleryImages(allImages);
        }
    }, [rawProjects, rawCategories]);


    // Extract unique project titles for filters
    const projectFilters = ["All", ...new Set(rawProjects.map(p => p.title))];

    // Smooth filter transition logic
    useEffect(() => {
        if (selectedProject === "All") {
            setFilteredImages(galleryImages);
        } else {
            setFilteredImages(galleryImages.filter(img => img.projectTitle === selectedProject));
        }
    }, [selectedProject, galleryImages]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!selectedImage?.gallery) return;

            if (e.key === "ArrowLeft") {
                setCurrentImageIndex((prev) => (prev - 1 + selectedImage.gallery.length) % selectedImage.gallery.length);
            } else if (e.key === "ArrowRight") {
                setCurrentImageIndex((prev) => (prev + 1) % selectedImage.gallery.length);
            } else if (e.key === "Escape") {
                setSelectedImage(null);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectedImage]);

    return (
        <div className="min-h-screen bg-background text-primary overflow-hidden relative selection:bg-secondary selection:text-white">

            {/* Background Decorations */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-secondary/5 blur-[120px] rounded-full mix-blend-screen animate-pulse-slow" />
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-blue-900/10 blur-[100px] rounded-full mix-blend-screen animate-pulse-slow" style={{ animationDelay: '2s' }} />
            </div>

            {/* Header Section */}
            <Container className="relative pt-24 md:pt-32 pb-8 md:pb-12 text-center z-10 px-4">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="flex items-center justify-center gap-4 mb-4 md:mb-6">
                        <span className="h-px w-8 md:w-12 bg-secondary/50"></span>
                        <span className="text-secondary text-xs md:text-sm font-bold tracking-[0.2em] uppercase">Visual Narrative</span>
                        <span className="h-px w-8 md:w-12 bg-secondary/50"></span>
                    </div>
                    {/* <h1 className="text-4xl md:text-7xl lg:text-8xl font-serif text-primary mb-6 md:mb-8 leading-tight">
                        Moments of <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-primary italic">Excellence</span>
                    </h1>
                    <p className="max-w-2xl mx-auto text-light text-sm md:text-lg leading-relaxed font-light px-4">
                        A curated collection of moments that define the Valletta experience.
                        Where architecture meets emotion and design serves life.
                    </p> */}
                    <h1 className="text-4xl md:text-7xl lg:text-8xl font-serif text-primary mb-6 md:mb-8 leading-tight font-bold">
                        Moments of <br />
                        <span className="">Excellence</span>
                    </h1>
                    <p className="text-secondary text-base md:text-lg leading-relaxed max-w-2xl mx-auto font-medium">
                        A curated collection of moments that define the Valletta experience.
                        Where architecture meets emotion and design serves life.
                    </p>
                </motion.div>
            </Container>

            {/* Filter Navigation */}
            {/* <div className="sticky top-0 z-40 py-4 md:py-6 mb-8 backdrop-blur-md">
                <Container className="px-2 md:px-4">
                 
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="flex flex-wrap justify-center items-center gap-2 sm:gap-3 w-full"
                    >
                        {projectFilters.map((projTitle) => (
                            <FilterButton
                                key={projTitle}
                                label={projTitle}
                                active={selectedProject === projTitle}
                                onClick={() => setSelectedProject(projTitle)}
                            />
                        ))}
                    </motion.div>
                </Container>
            </div> */}

            {projectsLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-12 h-12 border-2 border-secondary/30 border-t-secondary rounded-full animate-spin mb-4"></div>
                    <span className="text-light font-serif tracking-widest text-sm">LOADING GALLERY...</span>
                </div>
            ) : (
                <Container className="pb-32 min-h-screen relative z-10">
                    <AnimatePresence mode="wait">
                        {selectedProject === "All" ? (
                            /* Folder/Projects View */
                            <motion.div
                                key="folders"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.5 }}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12"
                            >
                                {rawProjects.map((project, index) => (
                                    <ProjectCard
                                        key={project._id}
                                        project={project}
                                        onClick={() => setSelectedProject(project.title)}
                                    />
                                ))}
                            </motion.div>
                        ) : (
                            /* Image Grid View */
                            <motion.div
                                key="images"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.5 }}
                            >
                                {/* Back Button & Breadcrumb */}
                                <div className="flex items-center gap-6 mb-12">
                                    <button
                                        onClick={() => setSelectedProject("All")}
                                        className="group flex items-center gap-3 text-secondary hover:text-primary transition-colors duration-300"
                                    >
                                        <div className="p-2 rounded-full border border-secondary/20 group-hover:border-secondary transition-all">
                                            <ArrowLeft size={18} />
                                        </div>
                                        <span className="text-xs font-bold uppercase tracking-[0.2em]">Back to Projects</span>
                                    </button>
                                    <div className="h-4 w-px bg-primary/10"></div>
                                    <h2 className="text-2xl font-serif text-primary">{selectedProject}</h2>
                                </div>

                                <motion.div layout className="columns-1 md:columns-2 lg:columns-3 gap-8 space-y-8">
                                    <AnimatePresence>
                                        {filteredImages.map((image, index) => (
                                            <motion.div
                                                layout
                                                key={image.id}
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                exit={{ opacity: 0, scale: 0.9 }}
                                                transition={{ duration: 0.5, delay: index * 0.05 }}
                                                className="break-inside-avoid relative group cursor-pointer overflow-hidden rounded-xl border hover:border-primary/40 border-transparent shadow-lg"
                                                onClick={() => { setSelectedImage(image); setCurrentImageIndex(image.projectIndex); }}
                                            >
                                                <div className="relative overflow-hidden">
                                                    <motion.img
                                                        whileHover={{ scale: 1.05 }}
                                                        transition={{ duration: 0.7 }}
                                                        src={image.src}
                                                        alt={image.title}
                                                        className="w-full h-auto object-cover"
                                                        loading="lazy"
                                                    />

                                                    {/* Hover Overlay */}
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-6">
                                                        <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                                            {/* <p className="text-secondary text-[10px] uppercase tracking-widest font-bold mb-2">{image.category}</p> */}
                                                            <h3 className="text-white text-xl font-serif leading-none">{image.title}</h3>
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))}
                                    </AnimatePresence>
                                </motion.div>
                                {filteredImages.length === 0 && (
                                    <div className="text-center py-20 text-light w-full">
                                        <h3 className="text-2xl font-serif text-primary mb-2">No Images Found</h3>
                                        <p className="text-light">Try selecting a different project.</p>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Container>
            )}

            {/* Immersive Lightbox */}
            {createPortal(
                <AnimatePresence>
                    {selectedImage && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[60] bg-white/98 backdrop-blur-xl flex  items-center justify-center p-0 md:p-10"
                            onClick={() => setSelectedImage(null)}
                        >
                            {/* Controls */}
                            <div className="absolute top-0 left-0 w-full p-6 flex justify-between items-center z-50">
                                <div className="text-primary">
                                    <span className="text-secondary text-xs uppercase tracking-widest block mb-1">Project Gallery</span>
                                    <span className="text-xl sm:text-2xl font-serif">{selectedImage.title}</span>
                                    {selectedImage.gallery && (
                                        <span className="text-xs text-light ml-3 font-mono">
                                            {currentImageIndex + 1} / {selectedImage.gallery.length}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
                                    className="p-2 sm:p-3 border border-primary/10 rounded-full hover:bg-primary/5 text-light hover:text-primary transition-all duration-300 group"
                                >
                                    <X className="group-hover:rotate-90 transition-transform duration-300 text-lg sm:text-xl" />
                                </button>
                            </div>

                            {/* Image Container */}
                            <motion.div
                                layoutId={`img-${selectedImage.id}`}
                                className="relative w-full h-full flex flex-col items-center justify-center p-4 md:p-12 mt-14"
                            >
                                <div className="relative flex items-center justify-center w-full h-full ">
                                    {/* Prev Button */}
                                    {selectedImage.gallery && selectedImage.gallery.length > 1 && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCurrentImageIndex((prev) => (prev - 1 + selectedImage.gallery.length) % selectedImage.gallery.length);
                                            }}
                                            className="absolute left-2 md:-left-4 z-50 p-2 md:p-3 bg-white/20 hover:bg-secondary rounded-full text-primary hover:text-white transition-all backdrop-blur-sm border border-primary/10 shadow-xl"
                                        >
                                            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
                                        </button>
                                    )}

                                    <motion.img
                                        key={currentImageIndex} // Add key to trigger animation on change
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.4, ease: "easeOut" }}
                                        src={selectedImage.gallery ? selectedImage.gallery[currentImageIndex] : selectedImage.src}
                                        alt={selectedImage.title}
                                        className="max-w-full max-h-full  object-contain shadow-2xl rounded-lg"
                                    />

                                    {/* Next Button */}
                                    {selectedImage.gallery && selectedImage.gallery.length > 1 && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setCurrentImageIndex((prev) => (prev + 1) % selectedImage.gallery.length);
                                            }}
                                            className="absolute right-2 md:-right-4 z-50 p-2 md:p-3 bg-white/20 hover:bg-secondary rounded-full text-primary hover:text-white transition-all backdrop-blur-sm border border-primary/10 shadow-xl"
                                        >
                                            <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
                                        </button>
                                    )}
                                </div>

                                {/* Detailed Description */}
                                {/* <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="mt-6 text-center max-w-6xl px-4"
                                >
                                    <p className="text-primary text-sm font-medium leading-relaxed line-clamp-2">
                                        {selectedImage.description}
                                    </p>
                                </motion.div> */}
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>,
                document.body
            )}
        </div >
    );
};

export default GalleryPage;
